// The Help pane's ui_search / ui_call (System context PR B). It is the page's
// own bridge (voice/uiBridge) over the mounted page's tools, with two changes:
//
// - A costly tool (meta.costly: spends AI credits, starts a run) needs the
//   pane's Confirm, as a click-confirmed tool does. It answers
//   needs_confirmation until the Confirm button runs it; the bridge drops a
//   model's own `confirmed`, so no model can approve it.
// - outline_control rings a control on screen (help/outline), so the pane can
//   show where something is without changing anything.
// - navigate_to_page takes the person to another page ("can we go to
//   Files?"), on every page, even one that registers no tools: the voice
//   provider's navigateToPage over the same NAV_PAGES list Douglas uses. It
//   changes nothing, so it needs no Confirm. The pane is app-level, so it and
//   its conversation stay open across the move (Adam, 14 Oct).
//
// The tools are read live from the page that is mounted now (getPageTools),
// so another section's tools never exist here.

import { createUIBridge, type UIBridge } from '../voice/uiBridge'
import type { VoiceUITool } from '../voice/types'
import { NAV_PAGES, type NavigateResult } from '../pageTools/PageToolsProvider'
import { outlineControl } from './outline'

export const OUTLINE_TOOL = 'outline_control'
export const NAVIGATE_TOOL = 'navigate_to_page'

/** Goes to a NAV_PAGES page, or says why not. */
export type HelpNavigate = (page: string) => NavigateResult

const json = (v: unknown) => JSON.stringify(v)

/** A costly tool behind the pane's Confirm. A click-confirmed tool already asks itself. */
export function withConfirm(tool: VoiceUITool): VoiceUITool {
  if (!tool.meta?.costly || tool.meta.confirm === 'click') return tool
  return {
    ...tool,
    meta: { ...tool.meta, confirm: 'click' },
    execute: (args) => {
      const { confirmed, ...rest } = args
      if (confirmed !== true) {
        return json({
          needs_confirmation: true,
          message: `${tool.description.split(/(?<=\.)\s/)[0].replace(/\.$/, '')}? This uses AI credits.`,
          confirm_label: 'Confirm',
          tool: tool.name,
          args: rest,
        })
      }
      return tool.execute(rest)
    },
  }
}

/** outline_control: ring a control on screen by its label. Changes nothing. */
export function outlineTool(outline: (label: string) => ReturnType<typeof outlineControl> = outlineControl): VoiceUITool {
  return {
    name: OUTLINE_TOOL,
    meta: { category: 'read', section: 'Help', keywords: ['show', 'where', 'point', 'highlight', 'find', 'outline'] },
    description: 'Show the user where a control is: ring it on screen by its on-screen label and scroll it into view. Changes nothing.',
    parameters: {
      type: 'object',
      properties: { label: { type: 'string', description: 'The control\'s label as the screen shows it, e.g. "Quick Filter".' } },
      required: ['label'],
      additionalProperties: false,
    },
    execute: (args) => {
      const label = String(args.label ?? '').trim()
      if (!label) return json({ error: 'label is required' })
      const r = outline(label)
      return r.ok ? json({ did: `Showed "${r.label}"` }) : json({ error: r.error, on_screen: r.candidates })
    },
  }
}

/** navigate_to_page: go to another page for this customer and job. Changes nothing on any page. */
export function navigateTool(navigate?: HelpNavigate, allowed?: string[]): VoiceUITool {
  const keys = allowed ?? Object.keys(NAV_PAGES)
  return {
    name: NAVIGATE_TOOL,
    meta: { category: 'nav', section: 'Navigation', keywords: ['go', 'go to', 'navigate', 'take me', 'open page', 'switch page', 'move'] },
    description:
      'Take the user to another page of Platinum for the current customer and job. The Help pane stays open. Pages: ' +
      keys.map((k) => `${k} (${NAV_PAGES[k].label})`).join('; ') +
      '. Changes nothing on any page.',
    parameters: {
      type: 'object',
      properties: { page: { type: 'string', enum: keys, description: 'The page key.' } },
      required: ['page'],
      additionalProperties: false,
    },
    execute: (args) => {
      const page = String(args.page ?? '').trim()
      if (!NAV_PAGES[page] || !keys.includes(page)) return json({ error: `unknown page "${page}". The pages are: ${keys.join(', ')}` })
      if (!navigate) return json({ error: 'going to another page is not available here' })
      const r = navigate(page)
      return json(r.ok ? { ok: true, did: `Went to ${r.shows}`, navigated: r.navigated, shows: r.shows } : { error: r.error })
    },
  }
}

/** The Help pane's bridge over the mounted page's tools, plus outline_control and navigate_to_page. */
export function createHelpBridge(
  getPageTools: () => VoiceUITool[],
  outline?: (label: string) => ReturnType<typeof outlineControl>,
  navigate?: HelpNavigate,
  allowedPages?: () => string[],
): UIBridge {
  const own = new Set([OUTLINE_TOOL, NAVIGATE_TOOL])
  return createUIBridge(() => [
    ...getPageTools().filter((t) => !own.has(t.name)).map(withConfirm),
    outlineTool(outline),
    navigateTool(navigate, allowedPages?.()),
  ])
}
